/**
 * L2b duel pay (docs/design/2026-10-11-gameplay-loop.md §4): every counted bout of the ring pays cultivation. A win pays
 * `win` x, a loss `loss` x of `base + perRank * foe rank`; `direct` of it lands at once, the rest goes to the combat qi
 * pool (systems/qiPool.ts) to be refined by sitting. The same foe pays 3 bouts a real day (day from dayReset.ts via
 * `cultivation.boostDay`), so the ring cannot be farmed. Listens to the player's bouts: ladder / arena results
 * (`ladder` phase 'result') and duelMatch / pvpDuel results (`pvpDuel` phase 'result'). Auto play never pays.
 */
import { rankFor } from '../cultivation';
import type { WorldEvent } from '../world';
import { dayRoll } from './dayRoll';
import type { SystemHost, WorldSystem } from './types';

export const DUEL_PAY = { base: 9, perRank: 2.5, win: 1, loss: 0.35, direct: 0.5, perFoeDay: 3 } as const;

/** Cultivation a bout pays: more for a win, more against a higher-ranked foe. */
export const duelPay = (foeRank: number, won: boolean): number =>
  (DUEL_PAY.base + DUEL_PAY.perRank * Math.max(0, foeRank)) * (won ? DUEL_PAY.win : DUEL_PAY.loss);

export class DuelPaySystem implements WorldSystem {
  readonly name = 'duelPay';
  day = Number.NaN;
  private count = new Map<string, number>();
  constructor(private readonly h: SystemHost) {}

  realDay(day: number): void {
    dayRoll(this, day, () => this.count.clear());
  }

  /** Bouts paid today against `foe`. */
  boutsToday(foe: string): number {
    return this.count.get(foe) ?? 0;
  }

  observe(e: WorldEvent): void {
    const w = this.h.world;
    if (w.auto.on) return;
    let foe: string;
    let won: boolean;
    let foeRank = rankFor(w.progress.cultivationSpent).index; // contenders are matched to the player
    if (e.type === 'ladder' && e.phase === 'result' && e.foe) {
      foe = e.foe;
      won = e.won === true;
    } else if (e.type === 'pvpDuel' && e.phase === 'result') {
      const me = w.player.id;
      if (e.a !== me && e.b !== me) return;
      const otherId = e.a === me ? e.b : e.a;
      foe = e.a === me ? e.names[1] : e.names[0];
      won = e.winner === me;
      const other = w.players.get(otherId);
      if (other) foeRank = rankFor(other.progress.cultivationSpent).index;
    } else return;
    const sys = w.sys;
    dayRoll(this, sys.cultivation.boostDay, () => this.count.clear());
    const n = this.count.get(foe) ?? 0;
    if (n >= DUEL_PAY.perFoeDay) return;
    this.count.set(foe, n + 1);
    const pay = duelPay(foeRank, won);
    const now = Math.round(pay * DUEL_PAY.direct);
    w.progress.cultivation += now;
    sys.qiPool.add(pay - now);
  }
}
